import { useEffect, useState } from "react";
import { Menu, Phone, X } from "lucide-react";
import { WhatsAppIcon } from "@/components/site/WhatsAppIcon";
import {
  LOGO,
  NAV,
  PHONE_DISPLAY,
  PHONE_TEL,
  waLink,
  onCall,
  onWhatsApp,
} from "@/lib/site";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/85 backdrop-blur-xl border-b border-white/10"
          : "bg-gradient-to-b from-black/70 to-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between h-16 md:h-20">
        <a href="#inicio" data-testid="header-logo" className="flex items-center gap-3 shrink-0">
          <img src={LOGO} alt="DG Guinchos Jundiaí - Guincho 24 horas" className="h-11 md:h-14 w-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.6)]" />
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="text-[13px] font-semibold uppercase tracking-widest text-zinc-300 hover:text-white transition-colors relative after:absolute after:left-0 after:-bottom-1.5 after:h-0.5 after:w-0 after:bg-brand hover:after:w-full after:transition-all"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-4">
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="header-phone"
            onClick={() => onCall("header")}
            className="flex flex-col items-end leading-none group"
          >
            <span className="text-[10px] uppercase tracking-[0.2em] text-brand font-bold">Atendimento 24h</span>
            <span className="font-display text-xl font-bold text-white group-hover:text-brand transition-colors flex items-center gap-1.5">
              <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
            </span>
          </a>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp"
            onClick={() => onWhatsApp("header")}
            className="inline-flex items-center gap-2 bg-wapp hover:bg-wapp-hover text-black font-display font-bold uppercase tracking-wider text-sm px-5 py-3 rounded-sm transition-all active:scale-95 shadow-[0_0_20px_rgba(37,211,102,0.35)]"
          >
            <WhatsAppIcon className="h-5 w-5" /> Chamar no WhatsApp
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-open"
          aria-label="Abrir menu"
          className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-sm border border-white/15 text-white"
        >
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden fixed inset-0 z-[60] transition-all duration-300 ${
          open ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        <div className="absolute inset-0 bg-black/80" onClick={() => setOpen(false)} />
        <div
          className={`absolute right-0 top-0 h-full w-[82%] max-w-sm bg-surface border-l border-white/10 p-6 flex flex-col transition-transform duration-300 ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between mb-8">
            <img src={LOGO} alt="DG Guinchos" className="h-12 w-auto" />
            <button onClick={() => setOpen(false)} aria-label="Fechar menu" data-testid="mobile-menu-close" className="h-11 w-11 inline-flex items-center justify-center rounded-sm border border-white/15">
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-3 border-b border-white/5 font-display text-2xl font-bold uppercase tracking-tight text-zinc-200 hover:text-brand transition-colors"
              >
                {n.label}
              </a>
            ))}
          </nav>
          <div className="mt-auto pt-6 space-y-3">
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { onWhatsApp("mobile-menu"); setOpen(false); }}
              className="flex items-center justify-center gap-2 bg-wapp text-black font-display font-bold uppercase text-lg py-4 rounded-sm"
            >
              <WhatsAppIcon className="h-6 w-6" /> WhatsApp 24h
            </a>
            <a
              href={`tel:${PHONE_TEL}`}
              onClick={() => { onCall("mobile-menu"); setOpen(false); }}
              className="flex items-center justify-center gap-2 bg-brand text-white font-display font-bold uppercase text-lg py-4 rounded-sm"
            >
              <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};
